import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
  useRouterState,
} from "@tanstack/react-router";
import { registerMuiLicense } from "@/utils/muiLicense";
import { type ReactNode, Suspense, lazy, useState } from "react";

/* eslint-disable-next-line no-restricted-imports */
import { IntercomProvider } from "react-use-intercom";

// fonts, imported here for the same reason `_app` imports them
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import {
  INTERCOM_API_BASE,
  INTERCOM_APP_ID,
  COOKIEYES_CLIENT_ID,
} from "@/config/constants";
import marketingFonts from "@/components/marketing/marketingFonts.css?url";
import { isCmsPreview } from "@/content/preview";
import { MARKETING_DOCUMENT_META } from "@/routing/marketingDocument";
import { useCmsPreview } from "@/routing/useCmsPreview";
import { installDomTranslationGuard } from "@/security/domTranslationGuard";
import PHProvider from "@/providers/PHProvider";
import { lightPaletteOptions } from "@/theme";
import { bootGtm, CONSENT_DEFAULTS, GTM_ID, GTM_START } from "@/tracking/gtm";

/**
 * Whether the document is showing the dashboard rather than a marketing page.
 * `_app` is pathless, so its pages are scattered across the path space and the
 * match list is the only thing that separates the two.
 *
 * Each caller subscribes for itself rather than being handed a prop, so a
 * move between the branches re-renders them and not the whole document.
 */
function useIsDashboard() {
  return useRouterState({
    select: (state) =>
      state.matches.some((match) => match.routeId.startsWith("/_app")),
  });
}

/**
 * The head's mark on a marketing document, for code that has to tell the two
 * kinds apart before React runs (`isMarketingDocument`): the dashboard's
 * Stripe preload stands down there.
 */
function MarketingDocumentMark() {
  const isDashboard = useIsDashboard();
  return isDashboard ? null : (
    <meta name={MARKETING_DOCUMENT_META} content="marketing" />
  );
}

const COOKIEYES_SCRIPT_ID = "cookieyes";

/**
 * CookieYes throws out of its own script on any host its account has not
 * registered, which is every host but production: localhost, previews and
 * staging, where no banner is drawn either way. `document.currentScript` is
 * that tag only while that script is on the stack, so answering the throw here
 * cannot swallow anyone else's error, and nothing is answered on production
 * because nothing is thrown there.
 */
const COOKIEYES_THROW_GUARD = `addEventListener("error",function(e){if(document.currentScript&&document.currentScript.id===${JSON.stringify(
  COOKIEYES_SCRIPT_ID
)}){e.preventDefault();e.stopImmediatePropagation();}});`;

/**
 * The cookie-consent banner, loaded the way the published site loads it: a
 * blocking `<script>` in the head, so CookieYes has its cookie gate installed
 * before any tag GTM later pulls in can write anything.
 *
 * Marketing documents only, because that is the only place the published site
 * loads it: dashboard.webshare.io serves GTM with no banner.
 */
/**
 * Rubik, which the marketing pages set and the dashboard does not.
 *
 * A `<link>` here rather than an import inside `MarketingShell`: the shell's
 * chunk is reachable from the client entry, so a stylesheet attached to it is
 * emitted into every document, the dashboard's included, where nothing renders
 * in Rubik. It carries no `precedence` either, which would make React hold the
 * page's own boundary until the sheet loaded and hide the prerendered markup
 * behind a script.
 */
function MarketingFonts() {
  const isDashboard = useIsDashboard();

  return isDashboard ? null : <link rel="stylesheet" href={marketingFonts} />;
}

function CookieYesBanner() {
  const isDashboard = useIsDashboard();

  if (isDashboard) {
    return null;
  }

  return (
    <>
      {/* Google's consent defaults first, before CookieYes hands Google the
          visitor's choice and before GTM starts. */}
      <script dangerouslySetInnerHTML={{ __html: CONSENT_DEFAULTS }} />
      {/* Ahead of the tag it guards: the throw is synchronous inside it. */}
      <script dangerouslySetInnerHTML={{ __html: COOKIEYES_THROW_GUARD }} />
      <script
        id={COOKIEYES_SCRIPT_ID}
        src={`https://cdn-cookieyes.com/client_data/${COOKIEYES_CLIENT_ID}/script.js`}
      />
      {/* Straight after CookieYes, as the published head runs GTM's snippet:
          CookieYes hands consent to Google only into a data layer that
          already exists (`GTM_START`). */}
      <script dangerouslySetInnerHTML={{ __html: GTM_START }} />
    </>
  );
}

/**
 * Intercom for marketing readers: the same app and the same id the dashboard
 * boots, mounted where `IntercomProviderInternal` is not. That one identifies
 * the signed-in profile and a marketing reader has no session to identify, so
 * this one boots anonymously and stands down wherever `_app` mounts its own.
 * `autoBoot` with no boot props sends what the published page sends: an app id
 * and a host, and no `hide_default_launcher`, so the floating launcher shows
 * the way it does on live. The dashboard's provider keeps hiding it.
 *
 * Here rather than in `MarketingShell` because the marketing suites render that
 * shell, and the loader inserts the widget script before the document's first
 * `<script>`, an element a jsdom document does not have.
 */
function MarketingIntercom({ children }: { children: ReactNode }) {
  const isDashboard = useIsDashboard();

  if (isDashboard) {
    return children;
  }

  return (
    <IntercomProvider
      appId={INTERCOM_APP_ID}
      apiBase={INTERCOM_API_BASE}
      autoBoot
    >
      {children}
    </IntercomProvider>
  );
}

registerMuiLicense();

// Guard against browser page-translation crashing React's reconciler (issue
// #11538). Installed at module scope so it runs before React's first commit.
installDomTranslationGuard();

/**
 * Whether this document is a CMS preview, on the server and again in the
 * browser.
 *
 * The server knows from the request it is inside; the browser learns from the
 * one line that server renders, below. A loader would answer it too, but a
 * loader on the root route makes every page in the app, the dashboard's
 * included, preload the server-function runtime and everything else the root
 * then depends on.
 */
const PREVIEW_MARKER = "window.__CMS_PREVIEW__=!0";

// Fetched for a preview alone, so no other page carries it.
const PreviewBar = lazy(() => import("@/components/marketing/PreviewBar"));

declare global {
  interface Window {
    __CMS_PREVIEW__?: boolean;
  }
}

function usePreviewDocument(): boolean {
  const [preview] = useState(() =>
    typeof window === "undefined"
      ? isCmsPreview()
      : window.__CMS_PREVIEW__ === true
  );
  return preview;
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { content: lightPaletteOptions.primary.main, name: "theme-color" },
    ],
  }),
  component: RootDocument,
});

function RootDocument() {
  const preview = usePreviewDocument();
  useCmsPreview(preview);
  return (
    <html lang="en" ref={bootGtm}>
      <head>
        <HeadContent />
        {/* Before hydration, so the browser answers the same as the server did. */}
        {preview ? (
          <script dangerouslySetInnerHTML={{ __html: PREVIEW_MARKER }} />
        ) : null}
        <MarketingFonts />
        <MarketingDocumentMark />
        <CookieYesBanner />
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        <PHProvider>
          <MarketingIntercom>
            <Outlet />
          </MarketingIntercom>
        </PHProvider>
        {preview ? (
          <Suspense fallback={null}>
            <PreviewBar />
          </Suspense>
        ) : null}
        <Scripts />
      </body>
    </html>
  );
}
